// frontend/src/pages/ParametrosSistema/GerenciarParametrosSistema.jsx

import React, { useState, useEffect } from "react";
import Template from "../../../components/Template";
import api from "../../../services/api";
import ConfirmationModal from "../../../components/ConfirmationModal";
import SuccessModal from "../../../components/SuccessModal";
import { maskPhone, maskCpfCnpj } from "../../../utils/masks";
import { useNavigate } from "react-router-dom";
import usePermission from "../../../hooks/usePermission";

const GerenciarParametrosSistema = () => {
    const navigate = useNavigate();
    // Estado para armazenar os parâmetros do sistema
    const [parametros, setParametros] = useState({
        nomeEmpresa: "",
        cnpj: "",
        quantidadeDiasVencimento: "",
        whatsapp: "",
        email: "",
        smtpEmail: "",
        senhaEmail: "",
        intervaloCapturaXMLNotaFiscal: "",
        horarioProcessamentoXML: "",
        quantidadeExecucoesPorDia: "",
        tipoAgendamento: "horario", // padrão
        dataInicioNF: "",
        dataFimNF: "",
        provedorNFeAtivo: "plugstorage", // padrão
        plugstorageToken: "",
        plugstorageIdToken: "",
        qiveApiId: "",
        qiveApiKey: "",
        // Configurações de atualização de eventos/status de NFe
        habilitarAtualizacaoEventos: false,
        horarioAtualizacaoEventos: "06:00",
        limiteAtualizacaoEventos: 50,
        diasAtualizacaoEventos: 30,
        // Configurações de NFSe
        habilitarSincronizacaoNFSe: false,
        intervaloCapturaXMLNFSe: 60,
        horarioProcessamentoNFSe: "02:00",
        quantidadeExecucoesPorDiaNFSe: 3,
        tipoAgendamentoNFSe: "intervalo",
        dataInicioNFSe: "",
        dataFimNFSe: "",
        limiteNFSePorExecucao: 50,
    });

    // Estados para controle do formulário
    const [isEditMode, setIsEditMode] = useState(false);

    // Modais
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    // Estados de erro
    const [errors, setErrors] = useState({});

    // Carregar os parâmetros ao montar o componente
    useEffect(() => {
        fetchParametros();
    }, []);

    const handleBack = () => {
        navigate('/configuracoes');
    };


    const fetchParametros = async () => {
        try {
            const response = await api.get("/parametrosSistema");
            setParametros({
                nomeEmpresa: response.data.nomeEmpresa || "",
                cnpj: response.data.cnpj || "",
                quantidadeDiasVencimento: response.data.quantidadeDiasVencimento || "",
                whatsapp: response.data.whatsapp || "",
                email: response.data.email || "",
                smtpEmail: response.data.smtpEmail || "",
                senhaEmail: response.data.senhaEmail || "",
                intervaloCapturaXMLNotaFiscal: response.data.intervaloCapturaXMLNotaFiscal || "",
                horarioProcessamentoXML: response.data.horarioProcessamentoXML || "",
                quantidadeExecucoesPorDia: response.data.quantidadeExecucoesPorDia || "",
                tipoAgendamento: response.data.tipoAgendamento || "horario",
                dataInicioNF: response.data.dataInicioNF ? response.data.dataInicioNF.slice(0, 10) : "",
                dataFimNF: response.data.dataFimNF ? response.data.dataFimNF.slice(0, 10) : "",
                provedorNFeAtivo: response.data.provedorNFeAtivo || "plugstorage",
                plugstorageToken: response.data.plugstorageToken || "",
                plugstorageIdToken: response.data.plugstorageIdToken || "",
                qiveApiId: response.data.qiveApiId || "",
                qiveApiKey: response.data.qiveApiKey || "",
                // Configurações de atualização de eventos/status de NFe
                habilitarAtualizacaoEventos: response.data.habilitarAtualizacaoEventos || false,
                horarioAtualizacaoEventos: response.data.horarioAtualizacaoEventos || "06:00",
                limiteAtualizacaoEventos: response.data.limiteAtualizacaoEventos || 50,
                diasAtualizacaoEventos: response.data.diasAtualizacaoEventos || 30,
                // Configurações de NFSe
                habilitarSincronizacaoNFSe: response.data.habilitarSincronizacaoNFSe || false,
                intervaloCapturaXMLNFSe: response.data.intervaloCapturaXMLNFSe || 60,
                horarioProcessamentoNFSe: response.data.horarioProcessamentoNFSe || "02:00",
                quantidadeExecucoesPorDiaNFSe: response.data.quantidadeExecucoesPorDiaNFSe || 3,
                tipoAgendamentoNFSe: response.data.tipoAgendamentoNFSe || "intervalo",
                dataInicioNFSe: response.data.dataInicioNFSe ? response.data.dataInicioNFSe.slice(0, 10) : "",
                dataFimNFSe: response.data.dataFimNFSe ? response.data.dataFimNFSe.slice(0, 10) : "",
                limiteNFSePorExecucao: response.data.limiteNFSePorExecucao || 50,
            });
        } catch (error) {
            console.error("Erro ao carregar parâmetros do sistema:", error);
            alert("Erro ao carregar parâmetros do sistema. Tente novamente.");
        }
    };

    // Manipulador de mudança nos inputs
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setParametros({ ...parametros, [name]: value });
    };

    // Handler específico para campo de telefone
    const handlePhoneChange = (e) => {
        const originalValue = e.target.value;
        const masked = maskPhone(originalValue);
        setParametros((prev) => ({ ...prev, whatsapp: masked }));
    };

    const handleCpfCnpjChange = (e) => {
        const value = e.target.value;
        // Aplica a máscara
        const maskedValue = maskCpfCnpj(value);
        setParametros((prev) => ({
            ...prev,
            cnpj: maskedValue,
        }));
    };

    // Validação do formulário
    const validateForm = () => {
        const newErrors = {};

        if (parametros.cnpj && !validateCNPJ(parametros.cnpj)) {
            newErrors.cnpj = "CNPJ inválido";
        }

        if (parametros.email && !validateEmail(parametros.email)) {
            newErrors.email = "Email inválido";
        }

        if (parametros.senhaEmail && parametros.senhaEmail.length < 5) {
            newErrors.senhaEmail = "A senha deve ter pelo menos 5 caracteres";
        }

        if (
            parametros.horarioProcessamentoXML &&
            !/^(?:[01]\d|2[0-3]):?[0-5]\d$/.test(parametros.horarioProcessamentoXML)
        ) {
            newErrors.horarioProcessamentoXML = "Formato de horário inválido (HH:MM)";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    // Função para validar CNPJ (simplificada)
    const validateCNPJ = (cnpj) => {
        // Remover caracteres não numéricos
        cnpj = cnpj.replace(/[^\d]+/g, "");

        if (cnpj.length !== 14) return false;

        // Elimina CNPJs invalidos conhecidos
        if (
            cnpj === "00000000000000" ||
            cnpj === "11111111111111" ||
            cnpj === "22222222222222" ||
            cnpj === "33333333333333" ||
            cnpj === "44444444444444" ||
            cnpj === "55555555555555" ||
            cnpj === "66666666666666" ||
            cnpj === "77777777777777" ||
            cnpj === "88888888888888" ||
            cnpj === "99999999999999"
        )
            return false;

        // Valida DVs
        let tamanho = cnpj.length - 2;
        let numeros = cnpj.substring(0, tamanho);
        let digitos = cnpj.substring(tamanho);
        let soma = 0;
        let pos = tamanho - 7;
        for (let i = tamanho; i >= 1; i--) {
            soma += numeros.charAt(tamanho - i) * pos--;
            if (pos < 2) pos = 9;
        }
        let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
        if (resultado !== parseInt(digitos.charAt(0))) return false;

        tamanho = tamanho + 1;
        numeros = cnpj.substring(0, tamanho);
        soma = 0;
        pos = tamanho - 7;
        for (let i = tamanho; i >= 1; i--) {
            soma += numeros.charAt(tamanho - i) * pos--;
            if (pos < 2) pos = 9;
        }
        resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
        if (resultado !== parseInt(digitos.charAt(1))) return false;

        return true;
    };

    // Função para validar Email
    const validateEmail = (email) => {
        const regex = /\S+@\S+\.\S+/;
        return regex.test(email);
    };

    // Submeter o formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowConfirmDialog(true);
        }
    };

    // Confirmar atualização
    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);
        try {
            await api.put("/parametrosSistema", parametros);
            setShowSuccessDialog(true);
        } catch (error) {
            console.error("Erro ao atualizar parâmetros:", error);
            alert("Erro ao atualizar parâmetros. Verifique os dados e tente novamente.");
        }
    };

    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    // Cancelar edição (neste caso, apenas limpar erros)
    const handleCancelEdit = () => {
        setErrors({});
    };

    return (
        <Template>
            <div className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">Configurações do Sistema</h1>
                </div>

                {/* Formulário de Configuração */}
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-4 bg-white p-6 rounded-md border border-slate-200"
                >
                    <div className="text-stone-600 text-base font-medium">
                        Atualizar Parâmetros
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Nome da Empresa */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Nome da Empresa</label>
                            <input
                                type="text"
                                name="nomeEmpresa"
                                value={parametros.nomeEmpresa}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                placeholder="Digite o nome da empresa"
                            />
                        </div>

                        {/* CNPJ */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">CNPJ</label>
                            <input
                                type="text"
                                name="cnpj"
                                value={parametros.cnpj}
                                onChange={handleCpfCnpjChange}
                                className={`px-3 py-2 border ${errors.cnpj ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite o CNPJ"
                            />
                            {errors.cnpj && <p className="text-red-600 text-sm">{errors.cnpj}</p>}
                        </div>

                        {/* Quantidade de Dias para Alertas */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Quantidade de Dias para Alertas de Vencimento
                            </label>
                            <input
                                type="number"
                                name="quantidadeDiasVencimento"
                                value={parametros.quantidadeDiasVencimento}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                placeholder="Digite a quantidade de dias"
                                min="0"
                            />
                        </div>

                        {/* WhatsApp */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">WhatsApp</label>
                            <input
                                type="text"
                                name="whatsapp"
                                value={parametros.whatsapp}
                                onChange={handlePhoneChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                placeholder="Digite o número do WhatsApp"
                            />
                        </div>

                        {/* Email */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Email</label>
                            <input
                                type="email"
                                name="email"
                                value={parametros.email}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.email ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite o email"
                            />
                            {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}
                        </div>

                        {/* SMTP de Email */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">SMTP de Email</label>
                            <input
                                type="text"
                                name="smtpEmail"
                                value={parametros.smtpEmail}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                placeholder="Digite o SMTP de email"
                            />
                        </div>

                        {/* Senha do Email */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Senha do Email</label>
                            <input
                                type="password"
                                name="senhaEmail"
                                value={parametros.senhaEmail}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.senhaEmail ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite a senha do email"
                            />
                            {errors.senhaEmail && (
                                <p className="text-red-600 text-sm">{errors.senhaEmail}</p>
                            )}
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Tipo de Agendamento
                            </label>
                            <select
                                name="tipoAgendamento"
                                value={parametros.tipoAgendamento}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            >
                                <option value="horario">Horário fixo diário</option>
                                <option value="intervalo">Intervalo em minutos</option>
                                <option value="vezes">Execuções distribuídas por dia</option>
                            </select>
                        </div>

                        {/* Provedor de NFe */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Provedor de Nota Fiscal Eletrônica
                            </label>
                            <select
                                name="provedorNFeAtivo"
                                value={parametros.provedorNFeAtivo}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                disabled={!isEditMode}
                            >
                                <option value="plugstorage">PlugStorage (TecnoSpeed)</option>
                                <option value="qive">Qive (Arquivei)</option>
                            </select>
                            <p className="text-xs text-slate-500">
                                Selecione o provedor para integração e sincronização de notas fiscais eletrônicas
                            </p>
                        </div>

                        {/* Credenciais PlugStorage */}
                        {parametros.provedorNFeAtivo === "plugstorage" && (
                            <>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">
                                        PlugStorage - Token
                                    </label>
                                    <input
                                        type="password"
                                        name="plugstorageToken"
                                        value={parametros.plugstorageToken}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                        placeholder="Digite o token do PlugStorage"
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">
                                        PlugStorage - ID Token
                                    </label>
                                    <input
                                        type="password"
                                        name="plugstorageIdToken"
                                        value={parametros.plugstorageIdToken}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                        placeholder="Digite o ID Token do PlugStorage"
                                        disabled={!isEditMode}
                                    />
                                </div>
                            </>
                        )}

                        {/* Credenciais Qive */}
                        {parametros.provedorNFeAtivo === "qive" && (
                            <>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">
                                        Qive - API ID
                                    </label>
                                    <input
                                        type="text"
                                        name="qiveApiId"
                                        value={parametros.qiveApiId}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                        placeholder="Digite o API ID do Qive"
                                        disabled={!isEditMode}
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">
                                        Qive - API Key
                                    </label>
                                    <input
                                        type="password"
                                        name="qiveApiKey"
                                        value={parametros.qiveApiKey}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                        placeholder="Digite a API Key do Qive"
                                        disabled={!isEditMode}
                                    />
                                </div>
                            </>
                        )}

                        {/* Intervalo de Captura de XML de Nota Fiscal */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Intervalo de Captura de XML de Nota Fiscal (Minutos)
                            </label>
                            <input
                                type="number"
                                name="intervaloCapturaXMLNotaFiscal"
                                value={parametros.intervaloCapturaXMLNotaFiscal}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                placeholder="Digite o intervalo em minutos"
                                min="1"
                            />
                        </div>

                        {/* Horário de Processamento do XML */}
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">
                                Horário de Processamento do XML
                            </label>
                            <input
                                type="time"
                                name="horarioProcessamentoXML"
                                value={parametros.horarioProcessamentoXML}
                                onChange={handleInputChange}
                                className={`px-3 py-2 border ${errors.horarioProcessamentoXML ? "border-red-600" : "border-slate-300"
                                    } rounded-md text-sm`}
                                placeholder="Digite o horário (HH:MM)"
                            />
                            {errors.horarioProcessamentoXML && (
                                <p className="text-red-600 text-sm">{errors.horarioProcessamentoXML}</p>
                            )}
                        </div>
                        {parametros.tipoAgendamento === "intervalo" && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm text-slate-700 font-medium">
                                    Intervalo de Execução (minutos)
                                </label>
                                <input
                                    type="number"
                                    name="intervaloCapturaXMLNotaFiscal"
                                    value={parametros.intervaloCapturaXMLNotaFiscal}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                    min="1"
                                    placeholder="Digite o intervalo em minutos"
                                />
                            </div>
                        )}
                        {parametros.tipoAgendamento === "horario" && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm text-slate-700 font-medium">
                                    Horário de Execução
                                </label>
                                <input
                                    type="time"
                                    name="horarioProcessamentoXML"
                                    value={parametros.horarioProcessamentoXML}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                    placeholder="Digite o horário (HH:MM)"
                                />
                            </div>
                        )}
                        {parametros.tipoAgendamento === "vezes" && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm text-slate-700 font-medium">
                                    Quantidade de Execuções por Dia
                                </label>
                                <input
                                    type="number"
                                    name="quantidadeExecucoesPorDia"
                                    value={parametros.quantidadeExecucoesPorDia}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                    min="1"
                                    placeholder="Digite a quantidade de execuções"
                                />
                            </div>
                        )}

                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Data Inicial (opcional para busca de NF)</label>
                            <input
                                type="date"
                                name="dataInicioNF"
                                value={parametros.dataInicioNF}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm text-slate-700 font-medium">Data Final (opcional para busca de NF)</label>
                            <input
                                type="date"
                                name="dataFimNF"
                                value={parametros.dataFimNF}
                                onChange={handleInputChange}
                                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                            />
                        </div>
                    </div>

                    {/* Seção: Sincronização Automática de NFSe (Nota Fiscal de Serviço) */}
                    <div className="border-t border-slate-200 pt-4 mt-4">
                        <h3 className="text-stone-600 text-base font-medium mb-4">
                            Sincronização Automática de NFSe (Nota Fiscal de Serviço)
                        </h3>
                        <p className="text-xs text-slate-500 mb-4">
                            Configurações para busca automática de Notas Fiscais de Serviço via API Qive.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Habilitar Sincronização NFSe */}
                            <div className="flex flex-col gap-1.5 md:col-span-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="habilitarSincronizacaoNFSe"
                                        checked={parametros.habilitarSincronizacaoNFSe}
                                        onChange={(e) => setParametros({ ...parametros, habilitarSincronizacaoNFSe: e.target.checked })}
                                        className="w-4 h-4 text-stone-600 border-slate-300 rounded focus:ring-stone-500"
                                    />
                                    <span className="text-sm text-slate-700 font-medium">
                                        Habilitar sincronização automática de NFSe
                                    </span>
                                </label>
                                <p className="text-xs text-slate-500 ml-6">
                                    Quando habilitado, o sistema buscará automaticamente as NFSe na API Qive conforme a configuração abaixo.
                                </p>
                            </div>

                            {/* Tipo de Agendamento NFSe */}
                            <div className="flex flex-col gap-1.5 md:col-span-2">
                                <label className="text-sm text-slate-700 font-medium">
                                    Tipo de Agendamento para NFSe
                                </label>
                                <select
                                    name="tipoAgendamentoNFSe"
                                    value={parametros.tipoAgendamentoNFSe}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                    disabled={!parametros.habilitarSincronizacaoNFSe}
                                >
                                    <option value="intervalo">Por Intervalo (a cada X minutos)</option>
                                    <option value="horario">Horário Fixo (uma vez por dia)</option>
                                    <option value="vezes">Múltiplas Vezes por Dia</option>
                                </select>
                                <p className="text-xs text-slate-500">
                                    {parametros.tipoAgendamentoNFSe === "intervalo" && "Executa a sincronização a cada intervalo de minutos definido."}
                                    {parametros.tipoAgendamentoNFSe === "horario" && "Executa a sincronização uma vez por dia no horário definido."}
                                    {parametros.tipoAgendamentoNFSe === "vezes" && "Executa a sincronização múltiplas vezes por dia, distribuídas entre 07:00 e 20:00."}
                                </p>
                            </div>

                            {/* Campos dinâmicos baseados no tipo de agendamento */}
                            {parametros.tipoAgendamentoNFSe === "intervalo" && (
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">
                                        Intervalo de Execução (minutos)
                                    </label>
                                    <input
                                        type="number"
                                        name="intervaloCapturaXMLNFSe"
                                        value={parametros.intervaloCapturaXMLNFSe}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                        min="1"
                                        placeholder="60"
                                        disabled={!parametros.habilitarSincronizacaoNFSe}
                                    />
                                    <p className="text-xs text-slate-500">
                                        Exemplo: 60 minutos = executa a cada 1 hora
                                    </p>
                                </div>
                            )}

                            {parametros.tipoAgendamentoNFSe === "horario" && (
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">
                                        Horário de Execução
                                    </label>
                                    <input
                                        type="time"
                                        name="horarioProcessamentoNFSe"
                                        value={parametros.horarioProcessamentoNFSe}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                        disabled={!parametros.habilitarSincronizacaoNFSe}
                                    />
                                    <p className="text-xs text-slate-500">
                                        Exemplo: 02:00 = executa todos os dias às 02:00 da manhã
                                    </p>
                                </div>
                            )}

                            {parametros.tipoAgendamentoNFSe === "vezes" && (
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-sm text-slate-700 font-medium">
                                        Quantidade de Execuções por Dia
                                    </label>
                                    <input
                                        type="number"
                                        name="quantidadeExecucoesPorDiaNFSe"
                                        value={parametros.quantidadeExecucoesPorDiaNFSe}
                                        onChange={handleInputChange}
                                        className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                        min="1"
                                        max="10"
                                        placeholder="3"
                                        disabled={!parametros.habilitarSincronizacaoNFSe}
                                    />
                                    <p className="text-xs text-slate-500">
                                        Exemplo: 3 vezes = executa às 07:00, 13:00 e 19:00
                                    </p>
                                </div>
                            )}

                            {/* Limite de NFSe por Execução */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm text-slate-700 font-medium">
                                    Limite de NFSe por Execução
                                </label>
                                <input
                                    type="number"
                                    name="limiteNFSePorExecucao"
                                    value={parametros.limiteNFSePorExecucao}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                    min="1"
                                    placeholder="50"
                                    disabled={!parametros.habilitarSincronizacaoNFSe}
                                />
                                <p className="text-xs text-slate-500">
                                    Quantidade máxima de NFSe processadas por execução (padrão: 50)
                                </p>
                            </div>

                            {/* Data Início NFSe */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm text-slate-700 font-medium">
                                    Data Início (opcional)
                                </label>
                                <input
                                    type="date"
                                    name="dataInicioNFSe"
                                    value={parametros.dataInicioNFSe}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                    disabled={!parametros.habilitarSincronizacaoNFSe}
                                />
                                <p className="text-xs text-slate-500">
                                    Data inicial para buscar NFSe. Deixe vazio para buscar dos últimos 30 dias.
                                </p>
                            </div>

                            {/* Data Fim NFSe */}
                            <div className="flex flex-col gap-1.5">
                                <label className="text-sm text-slate-700 font-medium">
                                    Data Fim (opcional)
                                </label>
                                <input
                                    type="date"
                                    name="dataFimNFSe"
                                    value={parametros.dataFimNFSe}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                    disabled={!parametros.habilitarSincronizacaoNFSe}
                                />
                                <p className="text-xs text-slate-500">
                                    Data final para buscar NFSe. Deixe vazio para buscar até hoje.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Seção: Atualização de Status de NFe via Eventos */}
                    {parametros.provedorNFeAtivo === "qive" && (
                        <>
                            <div className="border-t border-slate-200 pt-4 mt-4">
                                <h3 className="text-stone-600 text-base font-medium mb-4">
                                    Verificação de Cancelamento de NFe
                                </h3>
                                <p className="text-xs text-slate-500 mb-4">
                                    Consulta eventos na API Qive para identificar notas fiscais canceladas e atualizar o status automaticamente.
                                </p>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Habilitar Atualização de Eventos */}
                                    <div className="flex flex-col gap-1.5 md:col-span-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                name="habilitarAtualizacaoEventos"
                                                checked={parametros.habilitarAtualizacaoEventos}
                                                onChange={(e) => setParametros({ ...parametros, habilitarAtualizacaoEventos: e.target.checked })}
                                                className="w-4 h-4 text-stone-600 border-slate-300 rounded focus:ring-stone-500"
                                            />
                                            <span className="text-sm text-slate-700 font-medium">
                                                Habilitar verificação automática de cancelamentos
                                            </span>
                                        </label>
                                        <p className="text-xs text-slate-500 ml-6">
                                            Quando habilitado, o sistema verificará diariamente se as notas foram canceladas na SEFAZ
                                        </p>
                                    </div>

                                    {/* Horário de Execução */}
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-sm text-slate-700 font-medium">
                                            Horário de Execução
                                        </label>
                                        <input
                                            type="time"
                                            name="horarioAtualizacaoEventos"
                                            value={parametros.horarioAtualizacaoEventos}
                                            onChange={handleInputChange}
                                            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                            disabled={!parametros.habilitarAtualizacaoEventos}
                                        />
                                        <p className="text-xs text-slate-500">
                                            Horário diário para verificar eventos das notas
                                        </p>
                                    </div>

                                    {/* Limite de Notas */}
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-sm text-slate-700 font-medium">
                                            Limite de Notas por Execução
                                        </label>
                                        <input
                                            type="number"
                                            name="limiteAtualizacaoEventos"
                                            value={parametros.limiteAtualizacaoEventos}
                                            onChange={handleInputChange}
                                            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                            min="1"
                                            max="500"
                                            disabled={!parametros.habilitarAtualizacaoEventos}
                                        />
                                        <p className="text-xs text-slate-500">
                                            Quantidade máxima de notas a verificar por execução
                                        </p>
                                    </div>

                                    {/* Dias para Busca */}
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-sm text-slate-700 font-medium">
                                            Período de Busca (dias)
                                        </label>
                                        <input
                                            type="number"
                                            name="diasAtualizacaoEventos"
                                            value={parametros.diasAtualizacaoEventos}
                                            onChange={handleInputChange}
                                            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
                                            min="1"
                                            max="365"
                                            disabled={!parametros.habilitarAtualizacaoEventos}
                                        />
                                        <p className="text-xs text-slate-500">
                                            Verificar notas emitidas nos últimos X dias
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Botões do Formulário */}
                    <div className="flex items-center w-full gap-4">
                        <div className="flex gap-4 ml-auto">
                            <button
                                type="button"
                                className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                                onClick={handleBack}
                            >
                                <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                                <span>Voltar</span>
                            </button>

                            {usePermission('SALVAR_PARAMETRO') && (
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                                >
                                    <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                                    <span>Salvar</span>
                                </button>
                            )}
                        </div>
                    </div>
                </form>

                {/* Modal de Confirmação */}
                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onConfirm={handleConfirmSubmit}
                    onCancel={handleCancelSubmit}
                    message="Deseja salvar as alterações nos parâmetros do sistema?"
                    textButtonConfirm="Sim, Salvar"
                />

                {/* Modal de Sucesso */}
                <SuccessModal
                    isVisible={showSuccessDialog}
                    message="Parâmetros do sistema atualizados com sucesso!"
                    onClose={() => setShowSuccessDialog(false)}
                />
            </div>
        </Template>
    );
};

export default GerenciarParametrosSistema;
